// Choice chips — structured asks. Pure helpers shared by the chat state
// (use-chat), the BFF history mapping (messages API), and the chips UI
// (MessageRow/ChoiceChips). The wire shape is the harness contract: an
// ASSISTANT row carries `metadata.choices`; the answering USER turn carries
// `metadata.choiceAnswer` referencing the ask + tapped option.

export type ChoiceOption = {
  id: string;
  label: string;
  /**
   * A LINK chip (e.g. "Join this meeting"): tapping it opens this URL and sends nothing — no reply,
   * no `choiceAnswer`, no optimistic bubble, no selected state. Only present once validated http(s)
   * (mirrors `meetingLinkUrl` in `lib/meeting-notes-card.ts`); an option whose `url` fails that
   * validation is dropped entirely by `readMessageChoices` rather than falling back to an ordinary
   * chip, which would send its label as if it were a real answer.
   */
  url?: string;
  /**
   * ISO-8601 instant past which the option stops being drawn. Evaluated at RENDER time
   * (`liveChoiceOptions`), not here — history is cached, so baking the expiry into the parsed row
   * would freeze whatever was true the moment it was parsed.
   */
  expiresAt?: string;
};
export type MessageChoices = { askId: string; options: ChoiceOption[] };
export type ChoiceAnswer = {
  askId: string;
  optionId: string;
  /**
   * Only on a share offer's "Send to someone else": the person the user typed. Mirrors common-lib's
   * `ChoiceAnswer.recipient`; the server keeps it on that one option and drops it on any other.
   */
  recipient?: { email?: string; phone?: string; name?: string };
};

/**
 * The same http(s)-only check `meetingLinkUrl` (`lib/meeting-notes-card.ts`) applies to a meeting
 * link — duplicated rather than imported so this module stays dependency-free (mirrors
 * `lib/connector-card.ts`'s note on why: a unit test importing it must not drag in the app's runtime
 * graph). Returns the normalized href, or undefined for anything that isn't a well-formed http/https URL.
 */
function linkUrl(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value) return undefined;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Read + sanitize `metadata.choices` off a raw message entity: a valid askId and at least one
 * well-formed option, else undefined. An option needs {id, label}; `url`, when present, must survive
 * {@link linkUrl} or the WHOLE option is dropped (never falls back to an ordinary chip — a link chip
 * that sends its label instead of opening its URL is the bug this guards against). `expiresAt` is
 * carried through as-is (a string) with no date parsing here — see {@link liveChoiceOptions}.
 */
export function readMessageChoices(metadata: unknown): MessageChoices | undefined {
  const raw = (metadata as { choices?: unknown } | undefined)?.choices;
  if (!raw || typeof raw !== 'object') return undefined;
  const { askId, options } = raw as { askId?: unknown; options?: unknown };
  if (typeof askId !== 'string' || !askId || !Array.isArray(options)) return undefined;
  const clean: ChoiceOption[] = [];
  for (const o of options) {
    const { id, label, url, expiresAt } = (o ?? {}) as { id?: unknown; label?: unknown; url?: unknown; expiresAt?: unknown };
    if (typeof id !== 'string' || !id || typeof label !== 'string' || !label) continue;
    const option: ChoiceOption = { id, label };
    if (url !== undefined) {
      const validUrl = linkUrl(url);
      if (!validUrl) continue; // the whole option is dropped, not downgraded to a plain chip
      option.url = validUrl;
    }
    if (typeof expiresAt === 'string' && expiresAt) option.expiresAt = expiresAt;
    clean.push(option);
  }
  return clean.length > 0 ? { askId, options: clean } : undefined;
}

/**
 * Which of an ask's options are still worth drawing right now: an option with no `expiresAt` always
 * is; one with an unparseable or already-past `expiresAt` is dropped. Called at RENDER time
 * (`ChoiceChips`), not while parsing — a cached history row must re-evaluate its expiry on every
 * render rather than freezing whatever was true when the row first loaded. No live ticking: a chip
 * that expires while the tab sits open stays until the next render, which is fine — this is a
 * courtesy filter, not a countdown.
 */
export function liveChoiceOptions(options: readonly ChoiceOption[], now: number = Date.now()): ChoiceOption[] {
  return options.filter((o) => {
    if (!o.expiresAt) return true;
    const t = Date.parse(o.expiresAt);
    return Number.isFinite(t) && t > now;
  });
}

/** Read + sanitize `metadata.choiceAnswer` off a raw message entity. */
export function readChoiceAnswer(metadata: unknown): ChoiceAnswer | undefined {
  const raw = (metadata as { choiceAnswer?: unknown } | undefined)?.choiceAnswer;
  if (!raw || typeof raw !== 'object') return undefined;
  const { askId, optionId } = raw as { askId?: unknown; optionId?: unknown };
  if (typeof askId !== 'string' || !askId || typeof optionId !== 'string' || !optionId) return undefined;
  return { askId, optionId };
}

type ChoiceRow = { role?: string; choiceAnswer?: ChoiceAnswer };

/**
 * Selection derives from the transcript: an option is selected when any USER
 * row carries a `choiceAnswer` matching this ask. Covers the optimistic bubble
 * (stamped locally on send) and persisted history alike; multiple answers to
 * the same ask (chips stay tappable) all read as selected.
 */
export function selectedOptionIds(messages: readonly ChoiceRow[], askId: string): Set<string> {
  const ids = new Set<string>();
  for (const m of messages) {
    if (m.role === 'USER' && m.choiceAnswer?.askId === askId) ids.add(m.choiceAnswer.optionId);
  }
  return ids;
}

type ChoiceAskRow = ChoiceRow & { choices?: { askId: string } };

/**
 * The asks whose Choices card is SUPERSEDED: closed, drawn without a tap target. Shared rule across web,
 * iOS and Android — an UNANSWERED Choices card is live only when it belongs to the NEWEST ASSISTANT row
 * that carries `choices` in the conversation. Every older ASSISTANT row with `choices` whose ask has no
 * answer (no USER row with a matching `choiceAnswer.askId`, see {@link selectedOptionIds}) is superseded.
 *
 * - "Newest" is transcript order (last in `messages`), over ASSISTANT rows with `choices` only — USER rows
 *   and assistant rows without `choices` never displace the live card. It is not filtered by option expiry.
 * - An ANSWERED ask is never superseded (it renders unchanged, selection intact), wherever it sits.
 * - The newest ask's own askId is never superseded, even if an older row reuses the same askId.
 * - A `send-hold:` ask is never superseded and never counts as the newest (see `isHoldAsk`).
 * - A held decision's ask (`decision:<decisionId>`, see `isDecisionAsk`) is never closed by a newer card and
 *   never counts as the newest: each one is a DIFFERENT open decision, so a second parked task's card must not
 *   kill the first one's Run it / Drop it. It closes when it is ANSWERED instead (it is then in this set, and
 *   still draws its selection, since selection is read separately).
 *
 * Without this, a re-asked question leaves the older card tappable next to the newer one — two cards
 * asking the same thing, both waiting.
 */
export function supersededChoiceAskIds(messages: readonly ChoiceAskRow[]): Set<string> {
  let newestAskId: string | undefined;
  const answered = new Set<string>();
  for (const m of messages) {
    const askId = m.role === 'ASSISTANT' ? m.choices?.askId : undefined;
    if (askId && !isHoldAsk(askId) && !isDecisionAsk(askId)) newestAskId = askId;
    if (m.role === 'USER' && m.choiceAnswer?.askId) answered.add(m.choiceAnswer.askId);
  }
  const superseded = new Set<string>();
  for (const m of messages) {
    const askId = m.role === 'ASSISTANT' ? m.choices?.askId : undefined;
    if (!askId || isHoldAsk(askId)) continue;
    if (isDecisionAsk(askId)) {
      if (answered.has(askId)) superseded.add(askId);
    } else if (askId !== newestAskId && !answered.has(askId)) {
      superseded.add(askId);
    }
  }
  return superseded;
}

/**
 * A held decision's ask (`decision:<decisionId>`, minted one per decision by the Java harness's
 * `ReplyProjector.decisionChoices`). Its liveness is server state, not transcript order: several parked tasks
 * park several independent decisions, so a newer one is not a re-ask of an older one. A stale tap is safe — the
 * service refuses a decision that is no longer open. Pinned by `native-apps/shared/send-hold/contract.json`
 * (`superseded`) on all three surfaces. Inlined, like `isHoldAsk`.
 */
function isDecisionAsk(askId: string): boolean {
  return askId.startsWith('decision:');
}

/**
 * A waiting message's ask (`send-hold:<holdId>`, send rule) is never superseded and never counts as the newest
 * ask: its Cancel lives until the message goes, whatever is asked after it. Defensive — the hold's chip rides
 * its own `sendHoldCard`, not `choices` — and pinned by `native-apps/shared/send-hold/contract.json`
 * (`superseded`) on all three surfaces. Inlined rather than imported so this module keeps no imports of its own.
 */
function isHoldAsk(askId: string): boolean {
  return askId.startsWith('send-hold:');
}

type Choosable = { id?: string; finalId?: string; choices?: MessageChoices };

/** Patch the matching row's choices in place (matched by id OR persisted finalId); no-op when the row is gone. */
export function patchChoicesById<M extends Choosable>(
  messages: M[],
  messageId: string,
  choices: MessageChoices,
): M[] {
  const matches = (m: M) => m.id === messageId || m.finalId === messageId;
  if (!messages.some(matches)) return messages;
  return messages.map((m) => (matches(m) ? { ...m, choices } : m));
}
